/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Button,
  FormController, FormLabel, IconButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Drawer,
  Radio, RadioGroup, SelectionControlLabel, TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useFormik } from 'formik';

import PropTypes from 'prop-types';

const { func, bool, string } = PropTypes;

import { Constants } from '../utility/constants';

import { Close } from '@one-thd/sui-icons';

const EmailQuoteDrawer = ({
  open,
  setOpen,
  userId,
  quoteId,
  useEmailQuote,
  size,
  printOption,
  setPrintOption
}) => {
  const { svocID } = useThdCustomer();
  const [numberOfAdditionalEmails, setNumberOfAdditionalEmails] = useState(0);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [optionalEmails, setOptionalEmails] = useState([]);
  const [inputErrMessage, setInputErrMessage] = useState('');

  const [message, setMessage] = useState(
    Constants.QUOTE_EMAIL_MESSAGE,
  );

  const [handleEmailQuote, {
    data: emailQuoteData,
    loading: emailQuoteLoading,
  }] = useEmailQuote;

  const resetFields = () => {
    setOpen(false);
    setRecipientEmail('');
    setOptionalEmails([]);
    setMessage(Constants.QUOTE_EMAIL_MESSAGE);
    setPrintOption(Constants.PRINT_WITH_PRICE_DISCOUNT);
    setNumberOfAdditionalEmails(0);
  };

  useEffect(() => {
    if (!emailQuoteLoading && emailQuoteData?.emailQuote?.customerAccountId) {
      resetFields();
    }
  }, [emailQuoteLoading]);

  const removeEmail = (
    index,
    setErrors,
    setTouched,
  ) => {
    const optionalEmailsCopy = [...optionalEmails];
    optionalEmailsCopy.splice(index, 1);
    setOptionalEmails(optionalEmailsCopy);
    setNumberOfAdditionalEmails((prev) => {
      return prev - 1;
    });
    // Following lines are needed to reset all errors on form.
    setErrors({
      email: '',
      email0: '',
      email1: '',
      email2: '',
      email3: '',
    });
    setTouched(
      {
        email: false,
        email0: false,
        email1: false,
        email2: false,
        email3: false,
      },
      false,
    );
  };

  const updateEmail = (event, index) => {
    const optionalEmailsCopy = [...optionalEmails];
    optionalEmailsCopy[index] = event?.target?.value;

    setOptionalEmails(optionalEmailsCopy);
  };

  const formIsValid = () => {
    return [...optionalEmails, recipientEmail].every((value) => {
      return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
    });
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      email0: '',
      email1: '',
      email2: '',
      email3: '',
    },
    validateOnBlur: true,
    validate: () => {
      const errors = {
        email: '',
        email0: '',
        email1: '',
        email2: '',
        email3: '',
      };
      if (!recipientEmail) {
        errors.email = 'Please enter an email.';
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(recipientEmail)
      ) {
        errors.email = 'Invalid email address';
      }
      // eslint-disable-next-line array-callback-return
      optionalEmails.map((emailValue, index) => {
        if (!emailValue) {
          errors[`email${index}`] = 'Please enter an email.';
        } else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailValue)
        ) {
          errors[`email${index}`] = 'Invalid email address';
        }
      });
      return errors;
    },
  });

  const handleMessageChange = (event) => {
    const inputFormat = (/^[a-zA-Z0-9\s.,$?]*$/).test(event?.target?.value);
    if (inputFormat) {
      setInputErrMessage('');
      setMessage(event.target.value);
    } else {
      setInputErrMessage('We only allow letters (a-z), numbers and the following symbols ? , $ .');
    }
  };

  return (
    <Drawer size={size} open={open} onClose={() => resetFields()}>
      <DrawerHeader className="sui-font-w-bold sui-pt-4 sui-px-2 sui-py-2" title="Email Quote" onClose={() => resetFields()} />
      <DrawerBody>
        <>
          <div className="sui-font-w-bold">
            <Typography variant="body-base">
              Choose a Share Option and enter the email below to share your quote.
            </Typography>
          </div>
          <form>
            <div className="sui-grid sui-grid-cols-12 sui-gap-1">
              <div className="sui-col-span-12 sui-pt-4 sui-pb-4">
                <FormController>
                  <FormLabel id="demo-radio-buttons-group-label">
                    Share options
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby="radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={printOption}
                    onChange={(event) => {
                      setPrintOption(event.target.value);
                    }}
                  >
                    <SelectionControlLabel
                      value={Constants.PRINT_WITH_PRICE_DISCOUNT}
                      label="Price and Discounts"
                    >
                      <Radio />
                    </SelectionControlLabel>
                    <SelectionControlLabel
                      value={Constants.PRINT_WITH_PRICE_ONLY}
                      label="Price Only"
                    >
                      <Radio />
                    </SelectionControlLabel>
                    <SelectionControlLabel
                      value={Constants.PRINT_WITH_NO_PRICE}
                      label="No Price or Discounts"
                    >
                      <Radio />
                    </SelectionControlLabel>
                  </RadioGroup>
                </FormController>
              </div>
              <div className="sui-col-span-12">
                <TextField
                  id="email"
                  InputProps={{
                    inputProps: {
                      'data-testid': 'primaryEmail',
                    },
                  }}
                  status={formik.errors.email && formik.touched.email ? 'error' : undefined}
                  statusMessage={formik.touched.email ? formik.errors.email : undefined}
                  onBlur={formik.handleBlur}
                  onChange={(event) => {
                    setRecipientEmail(event.target.value);
                  }}
                  value={recipientEmail}
                  label="Recipients Email"
                  fullWidth
                />
              </div>

              <div className="sui-col-span-12">
                {numberOfAdditionalEmails <= 3 ? (
                  <div className="sui-col-span-8">
                    <Button
                      className="sui-underline sui-py-3"
                      data-testid="addAdditionalEmail"
                      onClick={() => {
                        setNumberOfAdditionalEmails((prev) => {
                          return prev + 1;
                        });
                        setOptionalEmails((prevState) => [...prevState, '']);
                      }}
                    >
                      Add Additional Email
                    </Button>
                  </div>
                ) : null}
                {optionalEmails.map((emailValue, index) => {
                  return (
                    <div
                      key={`optionalEmailField-${index}`}
                      className="sui-grid sui-grid-cols-12"
                    >
                      <div className="sui-col-span-11">
                        <TextField
                          id={`email${index}`}
                          InputProps={{
                            inputProps: {
                              'data-testid': `additionalEmail-${index}`,
                            },
                          }}
                          label={index === 0 ? 'Additional Recipients' : ''}
                          status={
                            formik.errors[`email${index}`]
                                    && formik.touched[`email${index}`]
                              ? 'error' : undefined
                          }
                          statusMessage={
                            formik.touched[`email${index}`]
                              ? formik.errors[`email${index}`]
                              : undefined
                          }
                          onBlur={formik.handleBlur}
                          onChange={(event) => {
                            updateEmail(event, index);
                          }}
                          value={optionalEmails[index]}
                          fullWidth
                        />
                      </div>
                      <div className="sui-col-span-1">
                        <div className={index === 0 ? 'sui-mt-8' : 'sui-mt-4'}>
                          <IconButton
                            icon={Close}
                            aria-label={`Remove Additional Email ${index}`}
                            data-testid={`removeButton-${index}`}
                            onClick={() => {
                              removeEmail(index, formik.setErrors, formik.setTouched);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="sui-col-span-12">
                <div className="sui-mt-2">
                  <TextField
                    id="message"
                    onChange={handleMessageChange}
                    status={inputErrMessage ? 'error' : undefined}
                    statusMessage={inputErrMessage || undefined}
                    value={message}
                    label="Message (Optional)"
                    fullWidth
                    InputProps={{
                      inputProps: {
                        'data-testid': 'message',
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </form>
        </>
      </DrawerBody>
      <DrawerFooter>
        <div className="sui-col-span-12 sui-pb-4">
          <Button
            type="submit"
            data-testid="submitButton"
            variant="primary"
            fullWidth
            onClick={(event) => {
              event.preventDefault();
              const variables = {
                userId,
                svocId: svocID,
                quoteId,
                quotePDF: {
                  toEmails: [...optionalEmails, recipientEmail],
                  message,
                  pdfScenario: printOption,
                }
              };
              handleEmailQuote({
                variables
              });
            }}
            disabled={!formIsValid()}
          >
            Send
          </Button>
        </div>
        <div className="sui-col-span-12 sui-w-full">
          <Button
            onClick={() => resetFields()}
            data-testid="cancelButton"
            variant="secondary"
            fullWidth
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

EmailQuoteDrawer.propTypes = {
  userId: string.isRequired,
  quoteId: string.isRequired,
  open: bool,
  setOpen: func,
  size: string.isRequired
};
EmailQuoteDrawer.defaultProps = {
  open: false,
  setOpen: () => {}
};
export { EmailQuoteDrawer };
